import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Shield, Loader2, Search, BarChart3, AlertTriangle, DollarSign,
  Scale, Users, TrendingUp, ChevronRight, Clock, CheckCircle2,
  XCircle, ArrowLeft, FileText, Eye, Gavel, Target
} from "lucide-react";
import { API } from "@/constants";
import { useAuth } from "@/contexts/AuthContext";
import { VerificationPanel, VerificationBadge, ConfidenceIndicator } from "@/components/shared/VerificationPanel";

const GRADE_COLORS = {
  "A+": "bg-emerald-600", "A": "bg-emerald-500", "A-": "bg-emerald-400",
  "B+": "bg-blue-600", "B": "bg-blue-500", "B-": "bg-blue-400",
  "C+": "bg-yellow-500", "C": "bg-yellow-600", "C-": "bg-orange-400",
  "D+": "bg-orange-500", "D": "bg-orange-600", "D-": "bg-red-400",
  "F": "bg-red-600", "N/A": "bg-slate-400",
};

const STATES = [
  "all", "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA",
  "KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM",
  "NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"
];

const CLAIM_TYPES = [
  { value: "auto", label: "Auto Insurance" },
  { value: "homeowners", label: "Homeowners" },
  { value: "commercial", label: "Commercial General Liability" },
  { value: "professional", label: "Professional Liability" },
  { value: "workers_comp", label: "Workers' Compensation" },
  { value: "umbrella", label: "Umbrella / Excess" },
  { value: "health", label: "Health Insurance" },
  { value: "life_disability", label: "Life & Disability" },
];

const ScoreRing = ({ score, grade, size = "lg" }) => {
  const radius = size === "lg" ? 54 : 36;
  const stroke = size === "lg" ? 8 : 6;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  const color = score >= 80 ? "#10B981" : score >= 60 ? "#3B82F6" : score >= 40 ? "#F59E0B" : "#EF4444";
  const dim = (radius + stroke) * 2;

  return (
    <div className="relative inline-flex items-center justify-center">
      <svg width={dim} height={dim} className="-rotate-90">
        <circle cx={radius + stroke} cy={radius + stroke} r={radius} stroke="#E2E8F0" strokeWidth={stroke} fill="none" />
        <circle cx={radius + stroke} cy={radius + stroke} r={radius} stroke={color} strokeWidth={stroke} fill="none"
          strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round" className="transition-all duration-1000" />
      </svg>
      <div className="absolute text-center">
        <p className={`font-bold ${size === "lg" ? "text-2xl" : "text-lg"}`}>{grade}</p>
        <p className={`text-slate-500 ${size === "lg" ? "text-sm" : "text-xs"}`}>{score}/100</p>
      </div>
    </div>
  );
};

const ScorecardSection = ({ title, icon: Icon, color, score, grade, items, itemKey }) => (
  <Card>
    <CardHeader className="pb-2">
      <div className="flex items-center justify-between">
        <CardTitle className="text-base flex items-center gap-2">
          <Icon className={`h-4 w-4 ${color}`} />
          {title}
        </CardTitle>
        <div className="flex items-center gap-2">
          <Badge className={`${GRADE_COLORS[grade] || "bg-slate-400"} text-white text-xs`}>{grade}</Badge>
          <span className="text-sm text-slate-500">{score}/100</span>
        </div>
      </div>
    </CardHeader>
    <CardContent>
      <div className="space-y-1.5">
        {(items || []).map((item, i) => (
          <div key={i} className="flex items-start gap-2 text-sm">
            <ChevronRight className="h-3.5 w-3.5 text-slate-400 mt-0.5 flex-shrink-0" />
            <span className="text-slate-700">{item}</span>
          </div>
        ))}
      </div>
    </CardContent>
  </Card>
);

const CarrierScorecardPage = () => {
  const { user } = useAuth();
  const [carrierName, setCarrierName] = useState("");
  const [state, setState] = useState("all");
  const [claimType, setClaimType] = useState("auto");
  const [injuryType, setInjuryType] = useState("");
  const [loading, setLoading] = useState(false);
  const [scorecard, setScorecard] = useState(null);
  const [history, setHistory] = useState([]);
  const [verificationStatus, setVerificationStatus] = useState("ai_generated");
  const [analysisId, setAnalysisId] = useState(null);

  useEffect(() => {
    axios.get(`${API}/carrier/history`, { withCredentials: true })
      .then(r => setHistory(r.data.scorecards || []))
      .catch(() => {});
  }, []);

  const generateScorecard = async () => {
    if (!carrierName.trim()) return;
    setLoading(true);
    setScorecard(null);
    try {
      const res = await axios.post(`${API}/carrier/scorecard`, {
        carrier_name: carrierName,
        state,
        claim_type: claimType,
        injury_type: injuryType || null,
      }, { withCredentials: true });
      setScorecard(res.data.scorecard);
      setAnalysisId(res.data.analysis_id);
      setVerificationStatus(res.data.verification_status);
      // Refresh history
      const h = await axios.get(`${API}/carrier/history`, { withCredentials: true });
      setHistory(h.data.scorecards || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const loadFromHistory = (item) => {
    setScorecard(item.scorecard);
    setAnalysisId(item.analysis_id);
    setVerificationStatus(item.verification_status || "ai_generated");
    setCarrierName(item.request?.carrier || "");
  };

  const sc = scorecard;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="carrier-scorecard-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-900 via-indigo-900 to-slate-900 text-white px-6 py-8">
        <div className="max-w-7xl mx-auto">
          <Link to="/tools/insurance" className="text-blue-300 text-sm hover:underline flex items-center gap-1 mb-4">
            <ArrowLeft className="h-3 w-3" /> Insurance Center
          </Link>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-800/50 rounded-xl">
              <Shield className="h-8 w-8 text-blue-300" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Insurance Carrier Scorecard</h1>
              <p className="text-blue-200">AI-powered carrier risk analysis & negotiation intelligence</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid lg:grid-cols-4 gap-6">
          {/* Input Panel */}
          <div className="lg:col-span-1 space-y-4">
            <Card>
              <CardHeader><CardTitle className="text-base">Analyze Carrier</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">Carrier Name</label>
                  <Input
                    value={carrierName}
                    onChange={e => setCarrierName(e.target.value)}
                    placeholder="e.g. State Farm"
                    data-testid="carrier-name-input"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">State</label>
                  <Select value={state} onValueChange={setState}>
                    <SelectTrigger data-testid="carrier-state-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All States</SelectItem>
                      {STATES.filter(s => s !== "all").map(s => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">Claim Type</label>
                  <Select value={claimType} onValueChange={setClaimType}>
                    <SelectTrigger data-testid="carrier-claim-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CLAIM_TYPES.map(ct => (
                        <SelectItem key={ct.value} value={ct.value}>{ct.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">Injury Type (optional)</label>
                  <Input
                    value={injuryType}
                    onChange={e => setInjuryType(e.target.value)}
                    placeholder="e.g. Soft tissue, TBI"
                    data-testid="carrier-injury-input"
                  />
                </div>
                <Button
                  onClick={generateScorecard}
                  disabled={loading || !carrierName.trim()}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  data-testid="generate-scorecard-btn"
                >
                  {loading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Analyzing...</> : <><Search className="h-4 w-4 mr-2" />Generate Scorecard</>}
                </Button>
              </CardContent>
            </Card>

            {/* History */}
            <Card>
              <CardHeader><CardTitle className="text-sm">Recent Analyses</CardTitle></CardHeader>
              <CardContent>
                {history.length === 0 && <p className="text-xs text-slate-500">No analyses yet</p>}
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {history.map((item, i) => (
                    <button
                      key={i}
                      onClick={() => loadFromHistory(item)}
                      className="w-full text-left p-2 rounded-lg border border-slate-200 hover:border-blue-300 hover:bg-blue-50/50 transition-all text-xs"
                      data-testid={`history-item-${i}`}
                    >
                      <p className="font-medium">{item.request?.carrier}</p>
                      <p className="text-slate-500">{item.scorecard?.overall_grade} — {item.request?.state} — {item.request?.claim_type}</p>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Results Panel */}
          <div className="lg:col-span-3">
            {!sc && !loading && (
              <Card className="border-dashed border-2">
                <CardContent className="py-16 text-center">
                  <Shield className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-slate-600 mb-2">Enter a carrier name to begin</h3>
                  <p className="text-sm text-slate-500">Get AI-powered risk analysis, settlement patterns, and negotiation intelligence</p>
                </CardContent>
              </Card>
            )}

            {loading && (
              <Card>
                <CardContent className="py-16 text-center">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
                  <p className="text-slate-600">Analyzing {carrierName}...</p>
                  <p className="text-sm text-slate-400 mt-1">Reviewing claims data, litigation history, and settlement patterns</p>
                </CardContent>
              </Card>
            )}

            {sc && !loading && (
              <div className="space-y-6">
                {/* Overall Score */}
                <Card className="border-blue-200">
                  <CardContent className="py-6">
                    <div className="flex items-center gap-8">
                      <ScoreRing score={sc.overall_score || 0} grade={sc.overall_grade || "N/A"} />
                      <div className="flex-1">
                        <h2 className="text-2xl font-bold" data-testid="carrier-name-result">{sc.carrier_name}</h2>
                        <div className="flex items-center gap-3 mt-2">
                          <Badge className={`${
                            sc.risk_level === "low" ? "bg-green-100 text-green-800" :
                            sc.risk_level === "moderate" ? "bg-yellow-100 text-yellow-800" :
                            "bg-red-100 text-red-800"
                          }`}>
                            Risk: {sc.risk_level?.toUpperCase()}
                          </Badge>
                          <VerificationBadge status={verificationStatus} />
                        </div>
                        <ConfidenceIndicator score={75} />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Score Sections */}
                <div className="grid md:grid-cols-2 gap-4">
                  <ScorecardSection
                    title="Claims Handling" icon={Clock} color="text-blue-600"
                    score={sc.claims_handling?.score} grade={sc.claims_handling?.grade}
                    items={[
                      `Avg Response: ${sc.claims_handling?.avg_response_days || '?'} days`,
                      `Avg Settlement: ${sc.claims_handling?.avg_settlement_days || '?'} days`,
                      `Denial Rate: ${sc.claims_handling?.denial_rate_pct || '?'}%`,
                      ...(sc.claims_handling?.findings || [])
                    ]}
                  />
                  <ScorecardSection
                    title="Bad Faith Indicators" icon={AlertTriangle} color="text-red-600"
                    score={sc.bad_faith_indicators?.score} grade={sc.bad_faith_indicators?.grade}
                    items={[
                      `Bad Faith Lawsuits (5yr): ${sc.bad_faith_indicators?.bad_faith_lawsuits_5yr || '?'}`,
                      `Regulatory Actions: ${sc.bad_faith_indicators?.regulatory_actions || '?'}`,
                      ...(sc.bad_faith_indicators?.known_tactics || []),
                      ...(sc.bad_faith_indicators?.red_flags || [])
                    ]}
                  />
                  <ScorecardSection
                    title="Settlement Patterns" icon={DollarSign} color="text-emerald-600"
                    score={sc.settlement_patterns?.score} grade={sc.settlement_patterns?.grade}
                    items={[
                      `Range: ${sc.settlement_patterns?.avg_settlement_range || '?'}`,
                      `Median: ${sc.settlement_patterns?.median_settlement || '?'}`,
                      `Lowball Frequency: ${sc.settlement_patterns?.lowball_frequency_pct || '?'}%`,
                      ...(sc.settlement_patterns?.patterns || [])
                    ]}
                  />
                  <ScorecardSection
                    title="Litigation Behavior" icon={Gavel} color="text-purple-600"
                    score={sc.litigation_behavior?.score} grade={sc.litigation_behavior?.grade}
                    items={[
                      `Willingness to Litigate: ${sc.litigation_behavior?.willingness_to_litigate_pct || '?'}%`,
                      `Trial Verdict Range: ${sc.litigation_behavior?.avg_trial_verdict_range || '?'}`,
                      `Appeal Rate: ${sc.litigation_behavior?.appeal_rate_pct || '?'}%`,
                      ...(sc.litigation_behavior?.observations || [])
                    ]}
                  />
                </div>

                {/* Defense Counsel */}
                <Card>
                  <CardHeader><CardTitle className="text-base flex items-center gap-2"><Users className="h-4 w-4 text-indigo-600" /> Defense Counsel Intelligence</CardTitle></CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div>
                        <p className="text-xs font-medium text-slate-500 mb-2">Preferred Firms</p>
                        {(sc.defense_counsel?.preferred_firms || []).map((f, i) => (
                          <p key={i} className="text-sm py-1 border-b border-slate-100 last:border-0">{f}</p>
                        ))}
                      </div>
                      <div>
                        <p className="text-xs font-medium text-slate-500 mb-2">Typical Strategies</p>
                        {(sc.defense_counsel?.typical_strategies || []).map((s, i) => (
                          <p key={i} className="text-sm py-1 border-b border-slate-100 last:border-0">{s}</p>
                        ))}
                      </div>
                      <div>
                        <p className="text-xs font-medium text-slate-500 mb-2">IME Doctors</p>
                        {(sc.defense_counsel?.ime_doctors_used || []).map((d, i) => (
                          <p key={i} className="text-sm py-1 border-b border-slate-100 last:border-0">{d}</p>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Recommendations & Tips */}
                <div className="grid md:grid-cols-2 gap-4">
                  <Card>
                    <CardHeader><CardTitle className="text-base flex items-center gap-2"><Target className="h-4 w-4 text-emerald-600" /> Recommendations</CardTitle></CardHeader>
                    <CardContent>
                      {(sc.recommendations || []).map((r, i) => (
                        <div key={i} className="flex items-start gap-2 text-sm py-1.5 border-b border-slate-100 last:border-0">
                          <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                          <span>{r}</span>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader><CardTitle className="text-base flex items-center gap-2"><Scale className="h-4 w-4 text-amber-600" /> Negotiation Tips</CardTitle></CardHeader>
                    <CardContent>
                      {(sc.negotiation_tips || []).map((t, i) => (
                        <div key={i} className="flex items-start gap-2 text-sm py-1.5 border-b border-slate-100 last:border-0">
                          <TrendingUp className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                          <span>{t}</span>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                </div>

                {/* Verification Panel */}
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => window.open(`${API}/export/carrier/${analysisId}`, '_blank')}
                    data-testid="export-pdf-btn"
                  >
                    <FileText className="h-4 w-4 mr-2" /> Export PDF
                  </Button>
                </div>
                <VerificationPanel
                  itemId={analysisId}
                  itemType="carrier_scorecard"
                  currentStatus={verificationStatus}
                  onStatusChange={setVerificationStatus}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CarrierScorecardPage;
